export type InstallOperatingSystem =
  | 'windows'
  | 'macos'
  | 'ios'
  | 'android'
  | 'linux'
  | 'unknown'

export type InstallBrowser =
  | 'chrome'
  | 'edge'
  | 'safari'
  | 'firefox'
  | 'samsung'
  | 'opera'
  | 'yandex'
  | 'unknown'

/**
 * Определённое окружение для инструкции установки PWA.
 */
export type InstallEnvironment = {
  operatingSystem: InstallOperatingSystem
  browser: InstallBrowser
  /** Уже открыто как установленное приложение */
  isStandalone: boolean
}

/**
 * Проверяет, запущено ли приложение в standalone / display-mode приложения.
 */
export const isAdminRunningAsInstalledApp = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  const displayStandalone = window.matchMedia?.(
    '(display-mode: standalone)'
  )?.matches
  const displayMinimal = window.matchMedia?.(
    '(display-mode: minimal-ui)'
  )?.matches
  // iOS Safari
  const iosStandalone = Boolean(
    // @ts-expect-error navigator.standalone — только iOS
    navigator?.standalone
  )
  return Boolean(displayStandalone || displayMinimal || iosStandalone)
}

/**
 * Определяет ОС и браузер по userAgent / platform (для подсказок установки).
 * @param userAgent — строка UA (по умолчанию navigator.userAgent)
 * @param platform — navigator.platform
 * @param maxTouchPoints — для iPadOS desktop UA
 */
export const detectInstallEnvironment = (
  userAgent: string = typeof navigator !== 'undefined'
    ? navigator.userAgent
    : '',
  platform: string = typeof navigator !== 'undefined'
    ? navigator.platform
    : '',
  maxTouchPoints: number = typeof navigator !== 'undefined'
    ? navigator.maxTouchPoints
    : 0
): InstallEnvironment => {
  const normalizedAgent = userAgent || ''

  const isIos =
    /iPad|iPhone|iPod/i.test(normalizedAgent) ||
    (platform === 'MacIntel' && maxTouchPoints > 1)

  let operatingSystem: InstallOperatingSystem = 'unknown'
  if (isIos) {
    operatingSystem = 'ios'
  } else if (/Android/i.test(normalizedAgent)) {
    operatingSystem = 'android'
  } else if (/Windows/i.test(normalizedAgent)) {
    operatingSystem = 'windows'
  } else if (/Mac OS X|Macintosh/i.test(normalizedAgent)) {
    operatingSystem = 'macos'
  } else if (/Linux/i.test(normalizedAgent)) {
    operatingSystem = 'linux'
  }

  let browser: InstallBrowser = 'unknown'
  // Порядок важен: Edg перед Chrome, YaBrowser перед Chrome, Samsung перед Chrome
  if (/YaBrowser/i.test(normalizedAgent)) {
    browser = 'yandex'
  } else if (/SamsungBrowser/i.test(normalizedAgent)) {
    browser = 'samsung'
  } else if (/Edg\//i.test(normalizedAgent)) {
    browser = 'edge'
  } else if (/OPR\/|Opera/i.test(normalizedAgent)) {
    browser = 'opera'
  } else if (/Firefox|FxiOS/i.test(normalizedAgent)) {
    browser = 'firefox'
  } else if (
    /Safari/i.test(normalizedAgent) &&
    !/Chrome|CriOS|Chromium/i.test(normalizedAgent)
  ) {
    browser = 'safari'
  } else if (/Chrome|CriOS|Chromium/i.test(normalizedAgent)) {
    browser = 'chrome'
  }

  return {
    operatingSystem,
    browser,
    isStandalone: isAdminRunningAsInstalledApp(),
  }
}
